import { Component, computed, inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { ButtonModule } from 'primeng/button'; // cspell:disable-line
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { ChartDoughnutComponent } from '../../chart-doughnut/chart-doughnut.component';
import { ImpactLegendItemComponent } from '../../impact-legend-item/impact-legend-item.component';
import { ImpactService } from '../../../services/impact.service';
import { ColorsService } from '../../../services/colors.service';
import { ChartSizeService } from '../../../services/chart-size.service';
import { ChartData, ImpactCategory, LegendItem } from '../../../models';
import { translateElementType } from '../../../utils/element-type-translation';
import { ReductionTipsService } from 'src/app/impact/services/reduction-tips.service';

/**
 * Non-transport types produced by `getOverviewDetailedBreakdown()`.
 * Everything else it emits is a transport category (plane, car, train…).
 */
const CATEGORY_BY_OVERVIEW_TYPE: Record<string, ImpactCategory> = {
  accommodationImpact: 'accommodation',
  activityImpact: 'activity',
  foodImpact: 'food',
  wasteImpact: 'waste',
};

/** Same grey as the "savings" segment of the comparison donuts. */
const AVOIDED_COLOR = '#CFD2CF';
const AVOIDED_TYPE = 'avoidedEmissions';

/** A legend row, plus the value it had before the selected tips applied. */
export type SimulatedLegendItem = LegendItem & { previousImpact?: number };

@Component({
  selector: 'app-reduction-tips-summary',
  standalone: true,
  imports: [
    TranslocoModule,
    ButtonModule,
    ChartDoughnutComponent,
    ImpactLegendItemComponent,
  ],
  templateUrl: './reduction-tips-summary.component.html',
})
export class ReductionTipsSummaryComponent {
  private impactService = inject(ImpactService);
  private colorsService = inject(ColorsService);
  private translocoService = inject(TranslocoService);
  protected reductionTipsService = inject(ReductionTipsService);
  chartSize = inject(ChartSizeService);
  formatCo2e = inject(FormatCo2eService);

  /** kg CO₂e avoided per breakdown category by the selected tips. */
  private savingsByType = this.reductionTipsService.savingsByType;
  private travellers = this.reductionTipsService.travellers;
  /** Display figures per traveller rather than for the whole trip. */
  private perPerson = this.reductionTipsService.perPerson;

  /** Trip breakdown as it stands today, before any tip is applied. */
  baseLegend = computed<LegendItem[]>(() => {
    const overview = this.impactService.overview();
    if (!overview) return [];

    const { detail, totalImpact } = overview;

    // Même dégradé par catégorie que le donut principal de la page
    const colors = this.colorsService.generateCategoryGroupedColors(
      detail.map((item) => ({
        category: CATEGORY_BY_OVERVIEW_TYPE[item.type] ?? 'transport',
        impact: item.impact,
        type: item.type,
      })),
    );

    return detail.map((item, index) => ({
      label: translateElementType(this.translocoService, item.type),
      type: item.type,
      impact: item.impact,
      color: colors[index],
      percentage:
        totalImpact > 0 ? Math.round((item.impact / totalImpact) * 100) : 0,
    }));
  });

  baseTotal = computed(() => this.impactService.overview()?.totalImpact ?? 0);

  /**
   * A category can never drop below zero, so the avoided total is the sum of
   * the clamped reductions — that keeps donut slices + avoided = base total.
   */
  private clampedSavings = computed<Record<string, number>>(() => {
    const savings = this.savingsByType();
    const clamped: Record<string, number> = {};

    this.baseLegend().forEach((item) => {
      const saved = savings[item.type];
      if (saved && saved > 0) {
        clamped[item.type] = Math.min(saved, item.impact);
      }
    });
    return clamped;
  });

  avoided = computed(() =>
    Object.values(this.clampedSavings()).reduce((sum, value) => sum + value, 0),
  );

  hasSelection = computed(() => this.avoided() > 0);

  newTotal = computed(() => Math.max(0, this.baseTotal() - this.avoided()));

  avoidedPercent = computed(() => {
    const total = this.baseTotal();
    return total > 0 ? Math.round((this.avoided() / total) * 100) : 0;
  });

  /** Reduced categories, then the avoided share as a grey slice. */
  simulatedLegend = computed<SimulatedLegendItem[]>(() => {
    const savings = this.clampedSavings();
    const total = this.baseTotal();

    const items: SimulatedLegendItem[] = this.baseLegend().map((item) => {
      const saved = savings[item.type] ?? 0;
      const impact = item.impact - saved;
      return {
        ...item,
        impact,
        percentage: total > 0 ? Math.round((impact / total) * 100) : 0,
        previousImpact: item.impact,
      };
    });

    const avoided = this.avoided();
    if (avoided > 0) {
      items.push({
        label: this.translocoService.translate(
          'reductionTips.simulation.avoided',
        ),
        type: AVOIDED_TYPE,
        impact: avoided,
        color: AVOIDED_COLOR,
        percentage: this.avoidedPercent(),
        previousImpact: 0,
      });
    }

    return items;
  });

  /**
   * Everything above is computed on trip totals — the basis of
   * `getOverviewDetailedBreakdown()`. Only what reaches the template follows
   * the view mode, so percentages stay identical in both readings.
   */
  private divisor = computed(() =>
    this.perPerson() ? Math.max(1, this.travellers()) : 1,
  );

  displayLegend = computed<SimulatedLegendItem[]>(() =>
    this.simulatedLegend().map((item) => ({
      ...item,
      impact: item.impact / this.divisor(),
      previousImpact:
        item.previousImpact === undefined
          ? undefined
          : item.previousImpact / this.divisor(),
    })),
  );

  displayBaseTotal = computed(() => this.baseTotal() / this.divisor());
  displayAvoided = computed(() => this.avoided() / this.divisor());
  displayNewTotal = computed(() => this.newTotal() / this.divisor());

  chartData = computed<ChartData[]>(() =>
    this.displayLegend()
      .filter((item) => item.impact > 0)
      .map((item) => ({
        label: item.label,
        value: Math.round(item.impact),
        color: item.color,
      })),
  );

  centerValue = computed(() =>
    this.formatCo2e.formatValue(this.displayNewTotal()),
  );

  centerLabel = computed(() => this.formatCo2e.getUnit(this.displayNewTotal()));

  /** Value and unit on the displayed basis — the dialog toggle names it. */
  co2e(value: number): string {
    return this.formatCo2e.format(value);
  }
}
